import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";

const ACCENT = "#c9a84c";
const SEP    = " ||| ";

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

function hexTint(hex, alpha) {
  const h = (hex || "#888").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

async function translateBatch(texts, tl) {
  const body = new URLSearchParams({ q: texts.join(SEP), sl: "en", tl, dt: "t", client: "gtx" });
  const res  = await fetch("https://translate.googleapis.com/translate_a/single", { method: "POST", body });
  const data = await res.json();
  const full  = data[0].map(c => c[0]).join("");
  const parts = full.split(/\s*\|\|\|\s*/);
  return texts.map((orig, i) => parts[i]?.trim() || orig);
}

function ArtCard({ item }) {
  const [imgFailed, setImgFailed] = useState(false);
  const hasImg = item.image && !imgFailed;

  return (
    <a
      className={`ns-card${!hasImg ? " ns-no-img" : ""}`}
      href={item.link} target="_blank" rel="noreferrer"
      style={{ "--accent": ACCENT, "--accent-tint": hexTint(ACCENT, 0.13) }}
    >
      {hasImg && (
        <div className="ns-card-img">
          <img src={item.image} alt={item.title} onError={() => setImgFailed(true)} />
        </div>
      )}
      <div className="ns-card-body">
        {item.date && <div className="ns-card-date">{fmtDate(item.date)}</div>}
        <div className="ns-card-title">{item.title}</div>
        {item.summary && (
          <p className="ns-card-summary">
            {item.summary.slice(0, 160)}{item.summary.length > 160 ? "…" : ""}
          </p>
        )}
        <div className="ns-card-src-row">
          {item.icon && <img className="ns-card-favicon" src={item.icon} alt="" />}
          <span className="ns-card-src" style={{ color: ACCENT }}>{item.source}</span>
        </div>
      </div>
    </a>
  );
}

export default function ArtNews() {
  const { t, lang } = useApp();
  const [items,       setItems]       = useState([]);
  const [displayed,   setDisplayed]   = useState([]);
  const [fetchStatus, setFetchStatus] = useState("loading");
  const [translating, setTranslating] = useState(false);

  const load = useCallback(() => {
    setFetchStatus("loading");
    axios.get("/api/news?feed=art_news")
      .then(r => { setItems(r.data.items || []); setFetchStatus("ready"); })
      .catch(() => setFetchStatus("error"));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Translate when items load or language changes
  useEffect(() => {
    if (fetchStatus !== "ready" || items.length === 0) return;

    if (lang !== "ar") {
      setDisplayed(items);
      return;
    }

    setTranslating(true);
    Promise.all([
      translateBatch(items.map(i => i.title),   "ar"),
      translateBatch(items.map(i => i.summary || ""), "ar"),
    ])
      .then(([titles, summaries]) => {
        setDisplayed(items.map((item, i) => ({
          ...item,
          title:   titles[i]   || item.title,
          summary: summaries[i] || item.summary,
        })));
      })
      .catch(() => setDisplayed(items))
      .finally(() => setTranslating(false));
  }, [items, lang, fetchStatus]);

  const isAr = lang === "ar";

  return (
    <>
      <div className="page-band">
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 6 }}>
            <img src="/Logoithad.jpg" alt="SPAU" style={{ height: 52, borderRadius: 8, opacity: 0.92 }} />
            <div>
              <h1 style={{ marginBottom: 2 }}>🎨 {t("artNews") || "Art News"}</h1>
              <p style={{ margin: 0 }}>{t("artNewsSub") || "Latest from the global art world"}</p>
            </div>
          </div>
        </div>
      </div>

      <section className="section container">
        {fetchStatus === "loading" && (
          <div className="empty-state">
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.8rem", color: ACCENT, marginBottom: 12 }} />
            <p>{t("loading")}</p>
          </div>
        )}
        {fetchStatus === "error" && (
          <div className="empty-state">
            <p style={{ marginBottom: 10 }}>{t("newsError") || "Could not load feed."}</p>
            <button className="btn btn-outline" onClick={load}>
              <i className="fa-solid fa-rotate-right" /> {t("retry") || "Retry"}
            </button>
          </div>
        )}
        {fetchStatus === "ready" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <p className="muted" style={{ margin: 0 }}>{items.length} {isAr ? "مقال" : "articles"}</p>
              {translating && (
                <span style={{ color: ACCENT, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "0.75rem" }} />
                  {isAr ? "جاري الترجمة…" : "Translating…"}
                </span>
              )}
            </div>
            <div className="ns-grid" dir={isAr ? "rtl" : "ltr"}>
              {(displayed.length ? displayed : items).map((item, i) => (
                <ArtCard key={i} item={item} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
